'use client'

import { useMarketBalances } from '@/hooks/wallets/use-market-balances'
import {
	DESTINATION_CHAINS,
	type LifiQuoteResponse,
	SOURCE_CHAINS,
	computeTotalFeeUsd,
	getLifiQuote,
	getLifiStatus,
} from '@/lib/api/lifi'
import { encodeErc20Approve, maxUint256 } from '@/lib/onchain/erc20'
import { executeWithRelayerFallback } from '@/lib/relayer-fallback'
import { invalidateMarketBalances } from '@/lib/wallets/invalidation'
import {
	type DepositWalletCall,
	buildErc20ApproveCall,
	deriveDepositWalletAddress,
	ensureDepositWalletReady,
	executeDepositWalletBatch,
} from '@/lib/wallets/polymarket-deposit'
import { useWallet } from '@/providers/WalletContext'
import {
	OperationType,
	type RelayClient,
	type SafeTransaction,
} from '@polymarket/builder-relayer-client'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import type { Address } from 'viem'

import { useTrading } from '@/providers/TradingProvider'
import { captureApiError } from '@/utils/sentry-helpers'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type WithdrawalStatus =
	| 'idle'
	| 'loading_quote'
	| 'quote_ready'
	| 'approving'
	| 'executing'
	| 'polling'
	| 'success'
	| 'error'

export interface WithdrawalQuote {
	totalFeeUsd: string
	estimatedOutput: string
	estimatedOutputMin: string
	timeEstimate: number
	inputAmount: string
	toolName: string
	toolLogoURI?: string
	sourceChainId: number
	destinationChainId: number
	raw: LifiQuoteResponse
}

export type SourceKey = 'polygon' | 'solana' | 'bsc'

export interface WithdrawalParams {
	amount: string // In USDC units (e.g., "10.50")
	sourceKey: SourceKey
	destinationChainId: number
	recipientAddress: string
	useMaxBalance?: boolean
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const POLYGON_RPC_URL = process.env.NEXT_PUBLIC_POLYGON_RPC_URL || 'https://polygon-rpc.com'

// ---------------------------------------------------------------------------
// ERC-20 helpers (raw ABI encoding — no extra deps)
// ---------------------------------------------------------------------------

/**
 * Read the ERC-20 `allowance(owner, spender)` for a token on Polygon via
 * a raw `eth_call`.
 */
async function checkErc20Allowance(
	tokenAddress: string,
	owner: string,
	spender: string
): Promise<bigint> {
	// allowance(address,address) → selector 0xdd62ed3e
	const data = `0xdd62ed3e${owner.slice(2).toLowerCase().padStart(64, '0')}${spender.slice(2).toLowerCase().padStart(64, '0')}`

	const res = await fetch(POLYGON_RPC_URL, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({
			jsonrpc: '2.0',
			id: 1,
			method: 'eth_call',
			params: [{ to: tokenAddress, data }, 'latest'],
		}),
	})

	const json = await res.json()
	if (json.error) throw new Error(json.error.message)
	return BigInt(json.result || '0')
}

// ---------------------------------------------------------------------------
// General helpers
// ---------------------------------------------------------------------------

/** Convert a LI.FI transactionRequest to the Polymarket SafeTransaction format */
function lifiTxToSafeTx(tx: {
	to: string
	data: string
	value: string
}): SafeTransaction {
	return {
		to: tx.to,
		data: tx.data,
		value: tx.value || '0',
		operation: OperationType.Call,
	}
}

/**
 * Execute a batch of SafeTransactions via the Polymarket relayer and assert
 * that the tx was actually mined. Throws if the relayer returns STATE_FAILED
 * or an empty txHash.
 */
async function executeAndAssert(
	relayClient: RelayClient,
	// biome-ignore lint/suspicious/noExplicitAny: walletClient type varies across providers
	walletClient: any,
	txs: SafeTransaction[],
	description: string
): Promise<string> {
	const response = await executeWithRelayerFallback(relayClient, walletClient, txs, description)
	const result = (await response.wait()) as {
		txHash?: string
		transactionHash?: string
		state?: string
	} | null

	const hash = result?.txHash || result?.transactionHash
	if (!hash) {
		const state = result?.state || 'unknown'
		throw new Error(
			`Transaction failed on-chain (state: ${state}). The relayer could not execute the transaction.`
		)
	}
	return hash
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useWithdrawal() {
	const [status, setStatus] = useState<WithdrawalStatus>('idle')
	const [quote, setQuote] = useState<WithdrawalQuote | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [txHashes, setTxHashes] = useState<Array<{ chainId: number; hash: string }>>([])

	const queryClient = useQueryClient()
	const {
		eoaAddress,
		solanaAddress,
		walletClient,
		publicClient,
		polymarketSafe,
		solanaConnection,
		solanaSigner,
		polymarketActiveWallet,
	} = useWallet()
	const { funderAddress, safeAddress: resolvedSafeAddress } = useTrading()

	// Balances
	const { balances } = useMarketBalances(['polymarket', 'kalshi'], {
		runtime: { addressOverrides: { polymarket: funderAddress } },
	})
	const polygonRawBalance = balances.polymarket?.cash.raw ?? 0n
	const solanaRawBalance = balances.kalshi?.cash.raw ?? 0n
	const polygonBalance = balances.polymarket?.cash.amount ?? 0
	const solanaBalance = balances.kalshi?.cash.amount ?? 0
	const totalBalance = polygonBalance + solanaBalance

	// -----------------------------------------------------------------------
	// Fetch quote
	// -----------------------------------------------------------------------

	// The Polymarket-side sender address: Safe for legacy users, deposit wallet
	// for new users — and for legacy users who switched to the deposit wallet.
	const polymarketSafeSourceAddress = (resolvedSafeAddress ??
		(polymarketActiveWallet === 'deposit' ? undefined : polymarketSafe)) as
		| `0x${string}`
		| undefined
	const polymarketSenderAddress =
		funderAddress ??
		polymarketSafeSourceAddress ??
		(eoaAddress ? deriveDepositWalletAddress(eoaAddress as Address) : undefined)

	const fetchQuote = useCallback(
		async (params: WithdrawalParams) => {
			if (!(polymarketSenderAddress && solanaAddress)) {
				throw new Error('Wallet not connected')
			}

			setStatus('loading_quote')
			setError(null)
			setQuote(null)

			try {
				const polygonRaw = polygonRawBalance
				const solanaRaw = solanaRawBalance

				const source = params.sourceKey
				const sourceChain = SOURCE_CHAINS[source]
				const sourceRaw = source === 'polygon' ? polygonRaw : solanaRaw
				const destChain = DESTINATION_CHAINS.find(c => c.id === params.destinationChainId)
				if (!destChain) throw new Error('Unsupported destination chain')

				let amountInSmallestUnit: string
				let amountNum: number

				if (params.useMaxBalance) {
					amountInSmallestUnit = sourceRaw.toString()
					amountNum = Number(sourceRaw) / 1_000_000
				} else {
					amountNum = Number.parseFloat(params.amount)
					if (Number.isNaN(amountNum) || amountNum <= 0) {
						throw new Error('Invalid amount')
					}
					const requestedRaw = BigInt(Math.floor(amountNum * 1_000_000))
					if (requestedRaw > sourceRaw) {
						throw new Error('Insufficient balance')
					}
					amountInSmallestUnit = requestedRaw.toString()
				}

				const fromAddress = source === 'polygon' ? polymarketSenderAddress : solanaAddress

				const response = await getLifiQuote({
					fromChain: sourceChain.key,
					toChain: destChain.key,
					fromToken: sourceChain.usdc,
					toToken: destChain.usdc,
					fromAmount: amountInSmallestUnit,
					fromAddress,
					toAddress: params.recipientAddress,
					slippage: 0.005,
					order: 'CHEAPEST',
				})

				const totalFeeUsd = computeTotalFeeUsd(response.estimate)

				const withdrawalQuote: WithdrawalQuote = {
					totalFeeUsd,
					estimatedOutput: response.estimate.toAmount,
					estimatedOutputMin: response.estimate.toAmountMin,
					timeEstimate: response.estimate.executionDuration || 30,
					inputAmount: amountNum.toFixed(2),
					toolName: response.toolDetails?.name || response.tool,
					toolLogoURI: response.toolDetails?.logoURI,
					sourceChainId: sourceChain.id,
					destinationChainId: destChain.id,
					raw: response,
				}

				setQuote(withdrawalQuote)
				setStatus('quote_ready')
				return withdrawalQuote
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to get quote'
				setError(message)
				setStatus('error')
				throw err
			}
		},
		[polymarketSenderAddress, solanaAddress, polygonRawBalance, solanaRawBalance]
	)

	// -----------------------------------------------------------------------
	// Execute withdrawal
	// -----------------------------------------------------------------------

	const execute = useCallback(
		async (relayClient: RelayClient | null) => {
			if (!quote) {
				setError('No quote available')
				setStatus('error')
				return
			}

			const txRequest = quote.raw.transactionRequest
			if (!txRequest) {
				setError('Quote does not contain transaction data')
				setStatus('error')
				return
			}

			setStatus('executing')
			setTxHashes([])

			try {
				const hashes: Array<{ chainId: number; hash: string }> = []
				const isPolygonSource = quote.sourceChainId === SOURCE_CHAINS.polygon.id

				if (isPolygonSource) {
					// -----------------------------------------------------------
					// Polygon → any chain via gas-sponsored path
					// -----------------------------------------------------------
					if (!polymarketSenderAddress) {
						throw new Error('Wallet not connected')
					}

					if (polymarketSafeSourceAddress) {
						// --- Safe path (legacy users) ---
						if (!relayClient) {
							throw new Error('Trading session not initialized. Please set up your wallet first.')
						}

						const approvalAddress = quote.raw.estimate.approvalAddress
						const fromTokenAddress = quote.raw.action.fromToken.address
						const fromAmount = BigInt(quote.raw.action.fromAmount)

						// -- Step 1: Approve the LI.FI contract if needed -----------
						const currentAllowance = await checkErc20Allowance(
							fromTokenAddress,
							polymarketSenderAddress,
							approvalAddress
						)

						if (currentAllowance < fromAmount) {
							setStatus('approving')

							const approvalTx: SafeTransaction = {
								to: fromTokenAddress,
								data: encodeErc20Approve(approvalAddress, maxUint256),
								value: '0',
								operation: OperationType.Call,
							}

							await executeAndAssert(
								relayClient,
								walletClient,
								[approvalTx],
								'Approve USDC for LI.FI'
							)
						}

						// -- Step 2: Execute the actual LI.FI swap/bridge tx --------
						setStatus('executing')

						const safeTx = lifiTxToSafeTx(txRequest)
						const txHash = await executeAndAssert(
							relayClient,
							walletClient,
							[safeTx],
							'Withdraw USDC via LI.FI'
						)

						hashes.push({ chainId: SOURCE_CHAINS.polygon.id, hash: txHash })
						setTxHashes([...hashes])
					} else {
						// --- Deposit-wallet path (new users / post-V2-cutover) ---
						//
						// Previously this branch just transferred pUSD from the deposit
						// wallet to `quote.raw.action.toAddress` on Polygon and returned
						// success. `action.toAddress` is the recipient on the DESTINATION
						// chain — for EVM bridges that's the user's own EOA, which exists
						// at the same address on Polygon too. So the transfer landed
						// pUSD on the user's Polygon EOA and the bridge never actually
						// happened. The Chance balance widget reads only the deposit
						// wallet, so the funds looked "missing" — see adamosx@gmail.com
						// 2026-05-13 incident for the prod repro that surfaced this.
						//
						// Correct flow (mirrors the Safe path above): approve LI.FI
						// diamond for the source token if needed, then execute the
						// full LI.FI `transactionRequest` from the deposit wallet via
						// `executeDepositWalletBatch`. LI.FI builds `txRequest` against
						// `fromAddress = polymarketSenderAddress` (deposit wallet for
						// this branch — see fetchQuote), so the calldata already
						// assumes the deposit wallet is the sender.
						if (!(walletClient && publicClient && eoaAddress)) {
							throw new Error('Wallet not connected')
						}
						try {
							const depositWalletAddress = deriveDepositWalletAddress(eoaAddress as Address)
							await ensureDepositWalletReady({ walletClient, publicClient, depositWalletAddress })

							const approvalAddress = quote.raw.estimate.approvalAddress as Address
							const fromTokenAddress = quote.raw.action.fromToken.address as Address
							const fromAmount = BigInt(quote.raw.action.fromAmount)

							const calls: DepositWalletCall[] = []

							// Step 1: approve LI.FI diamond if the deposit wallet hasn't
							// already given allowance. Same pattern as the Safe path —
							// `checkErc20Allowance` runs an `eth_call` directly so it
							// doesn't matter that the wallet hasn't been deployed yet.
							const currentAllowance = await checkErc20Allowance(
								fromTokenAddress,
								depositWalletAddress,
								approvalAddress
							)
							if (currentAllowance < fromAmount) {
								setStatus('approving')
								calls.push(buildErc20ApproveCall(fromTokenAddress, approvalAddress, maxUint256))
							}

							// Step 2: the actual bridge call — target + value + data
							// come straight from the LI.FI quote. Batched alongside the
							// approval in a single deposit-wallet userOp so the relayer
							// only fires once.
							setStatus('executing')
							calls.push({
								target: txRequest.to as Address,
								value: (txRequest.value as string) || '0',
								data: txRequest.data as `0x${string}`,
							})

							const { transactionHash } = await executeDepositWalletBatch(
								walletClient,
								calls,
								depositWalletAddress
							)
							hashes.push({ chainId: SOURCE_CHAINS.polygon.id, hash: transactionHash })
							setTxHashes([...hashes])
						} catch (err) {
							captureApiError(err, {
								endpoint: 'polymarket_deposit_withdraw',
								tags: { category: 'polymarket-deposit', flow: 'withdraw' },
								extra: { depositWallet: eoaAddress, userId: undefined },
							})
							throw err
						}
					}
				} else {
					// -----------------------------------------------------------
					// Solana → any chain: sign & broadcast Solana transaction
					// -----------------------------------------------------------
					if (!(solanaConnection && solanaSigner && solanaAddress)) {
						throw new Error('Solana wallet not available')
					}

					const { VersionedTransaction } = await import('@solana/web3.js')

					// LI.FI returns Solana tx data as base64 (no 0x prefix).
					// EVM tx data starts with "0x" and is hex-encoded.
					const rawData = txRequest.data
					const isHex = rawData.startsWith('0x')
					const txBuffer = isHex
						? Buffer.from(rawData.slice(2), 'hex')
						: Buffer.from(rawData, 'base64')
					const tx = VersionedTransaction.deserialize(new Uint8Array(txBuffer))

					const signedTx = await solanaSigner.addSignature(tx)

					let signature: string
					try {
						signature = await solanaConnection.sendRawTransaction(signedTx.serialize(), {
							skipPreflight: false,
							preflightCommitment: 'confirmed',
						})
					} catch (err) {
						const errorMessage = err instanceof Error ? err.message : String(err)
						if (
							errorMessage.includes(
								'Attempt to debit an account but found no record of a prior credit'
							)
						) {
							throw new Error(
								"We're having issues landing the transaction on Solana. The chain might be busy, please retry."
							)
						}
						throw err
					}

					// Wait for confirmation
					for (let i = 0; i < 30; i++) {
						const txStatus = await solanaConnection.getSignatureStatus(signature)
						if (
							txStatus.value?.confirmationStatus === 'confirmed' ||
							txStatus.value?.confirmationStatus === 'finalized'
						) {
							break
						}
						if (txStatus.value?.err) {
							throw new Error(`Transaction failed: ${JSON.stringify(txStatus.value.err)}`)
						}
						await new Promise(r => setTimeout(r, 1000))
					}

					hashes.push({ chainId: SOURCE_CHAINS.solana.id, hash: signature })
					setTxHashes([...hashes])
				}

				// ---------------------------------------------------------------
				// Poll LI.FI status for cross-chain transfers
				// ---------------------------------------------------------------
				if (quote.sourceChainId !== quote.destinationChainId && hashes.length > 0) {
					setStatus('polling')

					const txHash = hashes[0].hash
					let attempts = 0
					while (attempts < 120) {
						try {
							const statusResult = await getLifiStatus(
								txHash,
								quote.sourceChainId,
								quote.destinationChainId
							)

							if (statusResult.status === 'DONE') {
								if (statusResult.receiving?.txHash) {
									hashes.push({
										chainId: quote.destinationChainId,
										hash: statusResult.receiving.txHash,
									})
									setTxHashes([...hashes])
								}
								break
							}

							if (statusResult.status === 'FAILED') {
								console.warn('[Withdrawal] LI.FI transfer failed:', statusResult.substatusMessage)
								break
							}
						} catch {
							// Status endpoint might not be ready yet
						}

						await new Promise(r => setTimeout(r, 3000))
						attempts++
					}
				}

				setTxHashes(hashes)
				setStatus('success')

				invalidateMarketBalances(queryClient, ['polymarket', 'kalshi'])
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Withdrawal failed'
				setError(message)
				setStatus('error')
				console.error('[Withdrawal] Error:', err)
			}
		},
		[
			quote,
			solanaConnection,
			solanaSigner,
			solanaAddress,
			polymarketSafeSourceAddress,
			polymarketSenderAddress,
			eoaAddress,
			queryClient,
			walletClient,
			publicClient,
		]
	)

	// -----------------------------------------------------------------------
	// Reset
	// -----------------------------------------------------------------------

	const reset = useCallback(() => {
		setStatus('idle')
		setQuote(null)
		setError(null)
		setTxHashes([])
	}, [])

	return {
		// Balances
		polygonBalance,
		solanaBalance,
		totalBalance,
		// Quote
		quote,
		fetchQuote,
		// Execution
		execute,
		// Status
		status,
		error,
		txHashes,
		// Actions
		reset,
	}
}
